import React, { useEffect, useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const CookieContainer = styled.div`
  padding: 40px 20px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Inter', sans-serif;
  color: #333;
  line-height: 1.7;
`;

const CookieHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const CookieTitle = styled.h1`
  font-size: 48px;
  font-weight: 800;
  color: #2563eb;
  margin-bottom: 15px;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const CookieSubtitle = styled.p`
  font-size: 20px;
  color: #555;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const LastUpdated = styled.div`
  background-color: #f0f4f8;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 40px;
  text-align: center;
  font-weight: 600;
  color: #2563eb;
  font-size: 18px;
`;

const ContentSection = styled(motion.section)`
  margin-bottom: 40px;
  padding: 30px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  line-height: 1.7;
  font-size: 17px;
  color: #444;

  p {
    margin-bottom: 20px;
  }

  h2 {
    font-size: 24px;
    color: #2563eb;
    margin-bottom: 20px;
    font-weight: 700;
    border-bottom: 2px solid #e5e7eb;
    padding-bottom: 10px;
  }

  h3 {
    font-size: 20px;
    color: #374151;
    margin-bottom: 15px;
    font-weight: 600;
  }

  ul {
    margin-bottom: 20px;
    padding-left: 20px;
  }

  li {
    margin-bottom: 10px;
  }

  strong {
    color: #2563eb;
    font-weight: 700;
  }

  a {
    color: #2563eb;
    text-decoration: none;
    font-weight: 600;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Superscript = styled.sup`
  font-size: 0.6em;
  font-weight: 700;
  vertical-align: super;
  line-height: 0;
  color: inherit;
  position: relative;
  top: -0.2em;
`;

const CookiePolicy = () => {
  const containerRef = useRef(null);

  // Aggressive scroll function
  const scrollToTop = () => {
    // Multiple scroll methods for maximum compatibility
    window.scrollTo(0, 0);
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Also try scrolling the main content area
    const mainContent = document.querySelector('main');
    if (mainContent) {
      mainContent.scrollTop = 0;
    }
    
    // Scroll container into view
    if (containerRef.current) {
      try {
        containerRef.current.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'nearest' });
      } catch (e) {
        try {
          containerRef.current.scrollIntoView(true);
        } catch (e2) {
          // Last resort - just set scroll position
          const rect = containerRef.current.getBoundingClientRect();
          window.scrollTo(0, window.pageYOffset + rect.top);
        }
      }
    }
  };

  // Use useLayoutEffect to scroll immediately before paint
  useLayoutEffect(() => {
    scrollToTop();
  }, []);

  // Also use useEffect as backup after render with multiple attempts
  useEffect(() => {
    // Immediate scroll
    scrollToTop();
    
    // Multiple delayed attempts to catch any timing issues
    const timers = [
      setTimeout(scrollToTop, 0),
      setTimeout(scrollToTop, 10),
      setTimeout(scrollToTop, 50),
      setTimeout(scrollToTop, 100),
      setTimeout(scrollToTop, 200),
      setTimeout(scrollToTop, 500), // After animations might complete
    ];
    
    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, []);

  return (
    <CookieContainer ref={containerRef}>
      <CookieHeader>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <CookieTitle>Cookie and Tracking Technology Policy</CookieTitle>
          <CookieSubtitle>
            How we use cookies and tracking technologies on our platform
          </CookieSubtitle>
        </motion.div>
      </CookieHeader>

      <LastUpdated
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        Last Date Updated: 04/25/2025
      </LastUpdated>

      <ContentSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        <p>
          At M<Superscript>2</Superscript>eBooks we are committed to protecting your privacy and providing transparency about the technologies we use. This Cookie and Tracking Technology Policy explains how we use cookies and similar technologies when you visit our website, use our ebook platform, or interact with our services.
        </p>
      </ContentSection>

      <ContentSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        <h2>1. What Are Cookies?</h2>
        <p>
          Cookies are small text files stored on your device (computer, smart phone, tablet) when you visit a website. They allow us to recognize your device and remember your preferences and actions.
        </p>
      </ContentSection>

      <ContentSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8 }}
      >
        <h2>2. Types of Cookies We Use</h2>
        
        <h3>Essential Cookies</h3>
        <p>
          These cookies are necessary for our website and eBook platform to function. They enable basic functions such as page navigation, secure access, and digital rights management (DRM) for eBooks.
        </p>

        <h3>Performance & Analytics Cookies</h3>
        <p>
          These cookies help us understand how visitors interact with our site, including which pages are viewed most often and how users move through the site. We use services like Google Analytics to improve our site and content delivery.
        </p>

        <h3>Functionality Cookies</h3>
        <p>
          These remember your preferences (e.g., language, display settings) to personalize your experience.
        </p>

        <h3>Marketing & Advertising Cookies</h3>
        <p>
          These cookies track your online activity to help us deliver relevant advertising and measure campaign performance. We may partner with third-party ad networks and platforms (e.g., Google Ads, Meta) for this purpose.
        </p>
      </ContentSection>

      <ContentSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.0 }}
      >
        <h2>3. Tracking Technologies We May Use</h2>
        <p>
          We may also use tracking pixels (also known as web beacons), tags, and scripts to analyze trends, administer the site, track users' movements, and gather demographic information.
        </p>
      </ContentSection>

      <ContentSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.2 }}
      >
        <h2>4. How to Manage Cookies</h2>
        <p>
          You can control or delete cookies through your browser settings. You can also opt out of third-party tracking by visiting: <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out</a>
        </p>
        <p>
          Please note that disabling cookies may affect your user experience and some features of our site or eBooks may not function properly.
        </p>
      </ContentSection>

      <ContentSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.4 }}
      >
        <h2>5. Changes to This Policy</h2>
        <p>
          We may update this policy periodically. Changes will be posted on this page with an updated revision date. We encourage you to review this policy regularly.
        </p>
      </ContentSection>

      <ContentSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.6 }}
      >
        <h2>6. Contact Us</h2>
        <p>
          If you have any questions about this policy or how we use cookies, please contact us at:
        </p>
        <p>
          <strong>M<Superscript>2</Superscript>eBooks, LLC</strong><br />
          Email: <a href="mailto:help@m2ebooks.com">help@m2ebooks.com</a>
        </p>
      </ContentSection>
    </CookieContainer>
  );
};

export default CookiePolicy;

